import { useEffect, useRef } from "react";
import moment from "moment";
import telusConvivaAnalytics from "../../../shared/utils/convivaAnalytics";
import { setSessionStorage } from "../../../shared/utils/sessionStorage";
import {
  trackMediaComplete,
  trackMediaEnd,
  trackMediaPlay,
  trackConvivaCustomEvent,
  getCustomContextMetadata,
} from "../../../shared/analytics/media";
import { trackGenericAction } from "../../../shared/analytics/dataLayer";
import { DT_ATTRIBUTES } from "../../../shared/constants/dynatrace";
import { ANALYTICS_EVENT_TYPES, ANALYTICS_STORAGE_KEYS, LINK_INFO } from "../../../shared/constants/analytics";
import PlayerConstants from "../../../shared/constants/player";

import { usePlayerState } from "./playerState";

export const usePlayerAnalytics = (
  props,
  convivaContentSubType,
  triggerAnalyticsCalls,
  isRestartedStream,
  bookmark,
  isLookbackStream
) => {
  const { appProvider, userProfile, isInHome, featureToggles } = usePlayerState();
  const { isConvivaAppTrackerEnabled, isDynatraceTrackingEnabled } = featureToggles;
  const isTriggerAnalyticsCalled = useRef(false);
  const propsRef = useRef(props);
  // Adobe analytics based variables for XDM
  const ffCount = useRef(0);
  const rwCount = useRef(0);
  const isRestart = useRef(isRestartedStream ? 1 : 0);
  const chromecast = useRef(0);
  const upNext = useRef(0);
  const closedCaption = useRef(0);
  const hasBookmark = useRef(bookmark ? 1 : 0);
  const describedVideo = useRef(0);
  const hasLookback = useRef(isLookbackStream ? 1 : 0);
  const goLive = useRef(0);

  useEffect(() => {
    if (!telusConvivaAnalytics.isConvivaAnalyticsInitialized()) {
      telusConvivaAnalytics.initConvivaAnalytics(appProvider, userProfile, featureToggles);
    }

    telusConvivaAnalytics.reportParentalPinSuccess();

    telusConvivaAnalytics.initVideoSession(props, convivaContentSubType);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!isTriggerAnalyticsCalled.current) {
      isTriggerAnalyticsCalled.current = true;
      triggerAnalyticsCalls({ isDynatraceTrackingEnabled });
    }
  }, [triggerAnalyticsCalls, isDynatraceTrackingEnabled]);

  const trackConvivaAppTrackerEvent = (analyticsEventType, getMediaNodeMetaData) => {
    if (isConvivaAppTrackerEnabled) {
      trackConvivaCustomEvent(
        analyticsEventType,
        getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
      );
    }
  };

  /**
   * A callback function that will be triggered once the next episode has been loaded
   * @param {Boolean} isSessionCompleted - if true, the current media has been completely viewed
   * @param {String} linkName - a link(button) that was clicked to load the next episode
   * @param {Boolean} isDynatraceTrackingEnabled - flag to enable Dynatrace tracking
   */
  const loadNextEpisodeCallback = (isSessionCompleted = false, linkName = null, isDynatraceTrackingEnabled) => {
    if (isSessionCompleted) trackMediaComplete();
    else trackMediaEnd(null, isDynatraceTrackingEnabled);

    // set link to be an empty string when the next episode was auto-played
    setSessionStorage(ANALYTICS_STORAGE_KEYS.LINK, linkName ? `${linkName};${LINK_INFO.ON_DEMAND_PLAY}` : "");
  };

  /**
   * Tracks the playback start event.
   * @param {Object} initPlaybackTimestamp - The initial playback timestamp reference.
   * @param {Object} initPlayerTimestamp - The initial player timestamp reference.
   * @param {String} dtContentType - The content type for the playback.
   * @param {Boolean} isDynatraceTrackingEnabled - Flag to enable Dynatrace tracking.
   */
  const trackPlaybackStartTime = (
    initPlaybackTimestamp,
    initPlayerTimestamp,
    dtContentType,
    isDynatraceTrackingEnabled,
    retryInPlace
  ) => {
    if (initPlaybackTimestamp.current && initPlayerTimestamp.current) {
      const firstFrameTimestamp = moment().valueOf();
      // track player start event only if playback initiated successfully, this excludes playback error scenarios
      const playStartedEventAttr = {
        [DT_ATTRIBUTES.TIME_TO_PLAYBACK]: firstFrameTimestamp - initPlaybackTimestamp.current,
        [DT_ATTRIBUTES.TIME_TO_PLAYBACK_PLAYER_ONLY]: firstFrameTimestamp - initPlayerTimestamp.current,
        [DT_ATTRIBUTES.CONTENT_TYPE]: dtContentType,
        [DT_ATTRIBUTES.PLAYBACK_ORIGIN]: retryInPlace
          ? PlayerConstants.RETRY_STATE_VALUES.RETRY
          : PlayerConstants.RETRY_STATE_VALUES.CUSTOMER,
      };
      trackMediaPlay(playStartedEventAttr, isDynatraceTrackingEnabled);
      initPlayerTimestamp.current = null; // we need to reset this value to null to prevent user pause and resume events from being tracked as player playback start events
    }
  };

  const trackCloseClick = (getMediaNodeMetaData, isDynatraceTrackingEnabled) => {
    trackMediaEnd(() => {
      if (isConvivaAppTrackerEnabled) {
        trackConvivaCustomEvent(
          ANALYTICS_EVENT_TYPES.BOOKMARK,
          getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
        );
      }
    }, isDynatraceTrackingEnabled);
    telusConvivaAnalytics.onPlayerClosed();
  };

  const trackMediaStop = (getMediaNodeMetaData, isDynatraceTrackingEnabled) => {
    // Adobe XDM media stop event
    trackGenericAction(
      ANALYTICS_EVENT_TYPES.MEDIA_STOP,
      {
        media: getMediaNodeMetaData(),
        videoStates: {
          fastForward: ffCount.current,
          rewind: rwCount.current,
          restart: isRestart.current,
          airPlay: 0,
          chromeCast: chromecast.current,
          upNext: upNext.current,
          closedCaption: closedCaption.current,
          bookmark: hasBookmark.current,
          describedVideo: describedVideo.current,
          lookBack: hasLookback.current,
          goLive: goLive.current,
        },
      },
      isDynatraceTrackingEnabled
    );
  };

  return {
    propsRef,
    trackConvivaAppTrackerEvent,
    trackPlaybackStartTime,
    loadNextEpisodeCallback,
    trackCloseClick,
    hasBookmark,
    goLive,
    closedCaption,
    rwCount,
    ffCount,
    trackMediaStop,
    upNext,
    describedVideo,
  };
};
